import { useRef, useState } from 'react';
import { Check, ChevronDown, ShieldCheck } from 'lucide-react';
import { cn } from '@/shared/cn';
import {
  COMPOSER_CHROME_TRIGGER,
  COMPOSER_CHROME_TRIGGER_LABEL,
  DISABLED_INTERACTIVE,
  MENU_CONTENT,
} from '@/shared/ui-class';
import { useClickOutside } from '@/versions/v1/chrome/sidebar/AccountMenu';
import type { PermissionMode } from '@/versions/v1/shared/chat-types';
import { useChat } from '@/versions/v1/shared/ChatContext';

const MODE_COPY: Record<
  PermissionMode,
  { label: string; description: string }
> = {
  default: {
    label: 'Ask',
    description: 'Prompt before each tool call.',
  },
  bypassPermissions: {
    label: 'Auto',
    description: 'Run every tool automatically — no prompts.',
  },
};

export function PermissionSelector() {
  const { permissionMode, setPermissionMode } = useChat();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const activeCopy = MODE_COPY[permissionMode];

  useClickOutside(ref, () => setOpen(false), open);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={`Permission mode: ${activeCopy.label}`}
        title={`Permission mode — ${activeCopy.description}`}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          COMPOSER_CHROME_TRIGGER,
          open && 'bg-zinc-100 dark:bg-zinc-800/60',
          DISABLED_INTERACTIVE,
        )}
      >
        <ShieldCheck className="h-3.5 w-3.5 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
        <span className={cn('truncate text-left', COMPOSER_CHROME_TRIGGER_LABEL)}>
          {activeCopy.label}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0 text-zinc-400 dark:text-zinc-500',
            COMPOSER_CHROME_TRIGGER_LABEL,
          )}
        />
      </button>
      {open ? (
        <div className={cn(MENU_CONTENT, 'absolute bottom-full left-0 z-50 mb-2 w-[320px] overflow-hidden')}>
          {(Object.keys(MODE_COPY) as PermissionMode[]).map((mode) => {
            const copy = MODE_COPY[mode];
            const isActive = mode === permissionMode;
            return (
              <button
                key={mode}
                type="button"
                className="flex w-full cursor-pointer items-start gap-2.5 rounded-sm px-2 py-2 text-left text-sm outline-none hover:bg-zinc-100 dark:hover:bg-zinc-900"
                onClick={() => {
                  setPermissionMode(mode);
                  setOpen(false);
                }}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2 font-medium tracking-tight text-zinc-900 dark:text-zinc-100">
                    {copy.label}
                  </div>
                  <div className="text-xs text-muted">{copy.description}</div>
                </div>
                <div className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
                  {isActive ? (
                    <Check className="h-4 w-4 text-zinc-900 dark:text-zinc-100" />
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
